import { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { refreshToken } from "../services/auth"; 

interface AuthContextType {
  mobile: string | null;
  token: string | null;
  refreshToken: string | null;
  setMobile: (mobile: string | null) => void;
  setToken: (token: string | null, refreshToken?: string | null) => void; // Allow optional refreshToken
  fetched: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>({
  mobile: null,
  token: null,
  refreshToken: null,
  setMobile: () => {},
  setToken: () => {},
  fetched: false,
});

function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    console.log("[isTokenValid] Token payload:", payload.exp > currentTime); 
    return payload.exp > currentTime;
  } catch (error) {
    console.error("[isTokenValid] Error decoding token:", error);
    return false;
  }
}

function AuthProvider({ children }: { children: ReactNode }) {
  const [fetched, setIsFetched] = useState(false);
  const [mobile, setMobile] = useState<string | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [refreshTokenState, setRefreshTokenState] = useState<string | null>(null);
  useEffect(() => {
    const storedMobile = localStorage.getItem("mobile");
    let storedToken = getCookie("authToken");
    const storedRefreshToken = getCookie("refreshToken");
   //console.log("[AuthProvider] Initializing AuthProvider with stored values:",);
    if (storedMobile) setMobile(storedMobile);
    // console.log("storedToken", storedToken)
    // console.log("storedRefreshToken", storedRefreshToken)
    // console.log("isTokenValid(storedToken)", isTokenValid(storedToken))
    if (storedRefreshToken && (!storedToken || !isTokenValid(storedToken))) {
      // If access token is expired or missing, try to refresh it
      refreshToken(storedRefreshToken)
        .then((data) => {
          storedToken = data.accessToken; // Use accessToken from RefreshTokenResponse
          if (storedToken) { // Ensure storedToken is not null
            setTokenState(storedToken);
            setCookie("authToken", storedToken, 2); // Set new access token
          }
        })
        .catch((error) => {
          console.error("[AuthProvider] Failed to refresh token:", error);
          setTokenState(null);
          deleteCookie("authToken");
          deleteCookie("refreshToken");
        });
    } else if (storedToken && isTokenValid(storedToken)) {
      setTokenState(storedToken);
    }
    setRefreshTokenState(storedRefreshToken);
    setIsFetched(true);
  }, []);

  const setToken = (newToken: string | null, newRefreshToken: string | null = null) => {
    setTokenState(newToken);
    if (newToken) {
      setCookie("authToken", newToken, 2); // Access token: 2 days
    } else {
      deleteCookie("authToken");
    }

    if (newRefreshToken) {
      setRefreshTokenState(newRefreshToken);
      setCookie("refreshToken", newRefreshToken, 90); // Refresh token: 3 months
    } else if (newToken === null) {
      setRefreshTokenState(null);
      deleteCookie("refreshToken");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        mobile,
        setMobile,
        token,
        refreshToken: refreshTokenState,
        setToken,
        fetched,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Cookie helper functions
export const setCookie = (name: string, value: string, days: number = 90) => {
  try {
    if (typeof name !== "string" || !name || typeof value !== "string") {
      throw new Error(`Invalid cookie name or value: ${name}=${value}`);
    }
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    const encodedValue = encodeURIComponent(value);
    const cookieString = `${name}=${encodedValue};${expires};path=/;SameSite=Strict`;
    document.cookie = cookieString;
    return true;
  } catch (error) {
    console.error(`[setCookie] Failed to set cookie ${name}:`, error);
    return false;
  }
};

export const getCookie = (name: string): string | null => {
  try {
    if (typeof name !== "string" || !name) {
      return null;
    }
    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((c) => c.startsWith(`${name}=`));
    if (cookie) {
      const value = cookie.split("=").slice(1).join("=");
      const decodedValue = value ? decodeURIComponent(value) : null;
      return decodedValue;
    }
    return null;
  } catch (error) {
    console.error(`[getCookie] Failed to get cookie ${name}:`, error);
    return null;
  }
};

export const deleteCookie = (name: string) => {
  try {
    if (typeof name !== "string" || !name) {
      throw new Error(`Invalid cookie name: ${name}`);
    }
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict`;
    return true;
  } catch (error) {
    console.error(`[deleteCookie] Failed to delete cookie ${name}:`, error);
    return false;
  }
};

export { AuthProvider, useAuth, isTokenValid };